import BubbleCommon, { shortGoogleText } from '@op/components/BubbleCommon';
import ChannelLabel from '@op/components/ChannelLabel';
import {
  barSIcon,
  commonBarHIcon,
  lineIcon,
  tableIcon,
} from '@op/constants/chart';
import { languageRenderHelper } from '@op/pages/download/renderHelper';
import { getPercentageInfo } from '@op/utils';
import { DBO_V, DBO_Y } from '@op/utils/format';
import { dealWithOthersName } from '@op/utils/languageValue';
import { percentageFormat, reviewsFormat } from '@op/utils/value';
import React from 'react';
import { AnalysisSummaryTypeTab } from '../types';

export const analysisSummaryTab: (
  formatMessage: any,
  filterKeys?: AnalysisSummaryTypeTab[],
  value?: AnalysisSummaryTypeTab,
) => {
  text: string | any;
  value: AnalysisSummaryTypeTab;
}[] = (formatMessage, filterKeys, value) => {
  const list: {
    text: string | any;
    value: AnalysisSummaryTypeTab;
  }[] =
    [
      {
        text: (
          <div className="dbo-inlineflex">
            <span className="dbo-analysis-summary-text">
              {formatMessage({ id: 'Total Summary' })}
            </span>
          </div>
        ),
        value: 'total',
      },
      {
        text: (
          <div className="dbo-inlineflex">
            <BubbleCommon
              tipsMap={shortGoogleText}
              selected={value === 'google play'}
              showIcon
            >
              <ChannelLabel
                channel_name={'google play'}
                showText
                textClassName={'dbo-analysis-summary-text'}
              />
            </BubbleCommon>
          </div>
        ),
        value: 'google play',
      },
      {
        text: (
          <div className="dbo-inlineflex">
            <ChannelLabel
              channel_name={'app store'}
              showText
              textClassName={'dbo-analysis-summary-text'}
            />
          </div>
        ),
        value: 'app store',
      },
      {
        text: (
          <div className="dbo-inlineflex">
            <ChannelLabel
              channel_name={'steam'}
              showText
              textClassName={'dbo-analysis-summary-text'}
              replaceText={formatMessage({ id: 'Steam Analysis' })}
            />
          </div>
        ),
        value: 'steam',
      },
      // {
      //   text: (
      //     <div className="dbo-inlineflex">
      //       <span className="dbo-analysis-summary-text">
      //         {formatMessage({ id: 'Community Analysis' })}
      //       </span>
      //     </div>
      //   ),
      //   value: 'community',
      // },
    ];

  return list?.filter((item) => !filterKeys?.includes(item.value));
};

export const AnalysisChartTabType: {
  [key: string]: string;
} = {
  total: '',
  social_media: 'social',
  news: 'news',
};

export const AnalysisChartsConfig: { [key in AnalysisSummaryTypeTab]?: any } = {
  total: [
    {
      title: 'Language',
      operations: [barSIcon, lineIcon, tableIcon],
      defaultOperation: 'sentiment-s-bar',
      type: 'language',
      chart: {
        barAndLine: {
          tooltipConfig: {
            title: 'Mentions',
            showTotal: true,
          },
        },
        sentimentBar: {
          seriesConfig: [
            {
              name: 'Negative',
              type: 'bar',
              stack: 'total',
              color: '#F17C86',
              barMaxWidth: 30,
              label: {
                show: true,
                position: 'left',
                formatter: (params: {
                  value: number;
                  dataIndex: number;
                  data: { total: number };
                }) => {
                  const { value, data } = params;
                  if (value && data?.total) {
                    const formattedValue = Math.abs(value);
                    return getPercentageInfo(formattedValue, data?.total, 2);
                  }
                  return '';
                },
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'negativeMentions',
            },
            {
              name: 'Neutral',
              type: 'bar',
              stack: 'total',
              color: '#6E7794',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'neutralMentions',
            },
            {
              name: 'Positive',
              type: 'bar',
              stack: 'total',
              color: '#69D19C',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'positiveMentions',
            },
          ],
          tooltipConfig: {
            showShare: true,
          },
          rightTitle: 'Mentions',
        },
      },
    },
    {
      title: 'Channel',
      operations: [barSIcon, lineIcon, tableIcon],
      defaultOperation: 'sentiment-s-bar',
      type: 'channel',
      chart: {
        sentimentBar: {
          seriesConfig: [
            {
              name: 'Negative',
              type: 'bar',
              stack: 'total',
              color: '#F17C86',
              barMaxWidth: 30,
              label: {
                show: true,
                position: 'left',
                formatter: (params: {
                  value: number;
                  dataIndex: number;
                  data: { total: number };
                }) => {
                  const { value, data } = params;
                  if (value && data?.total) {
                    const formattedValue = Math.abs(value);
                    return getPercentageInfo(formattedValue, data?.total, 2);
                  }
                  return '';
                },
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'negativeMentions',
            },
            {
              name: 'Neutral',
              type: 'bar',
              stack: 'total',
              color: '#6E7794',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'neutralMentions',
            },
            {
              name: 'Positive',
              type: 'bar',
              stack: 'total',
              color: '#69D19C',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'positiveMentions',
            },
          ],
          tooltipConfig: {
            showShare: true,
          },
          rightTitle: 'Mentions',
        },
      },
    },
  ],
  'google play': [
    {
      title: 'Language',
      operations: [commonBarHIcon, lineIcon, tableIcon],
      defaultOperation: 'common-bar',
      type: 'language',
      chart: {
        commonBar: {
          tooltipConfig: {
            title: 'Reviews',
            showTotal: false,
          },
          seriesConfig: [
            {
              name: '1',
              type: 'bar',
              stack: 'total',
              color: '#F17C86',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings1',
            },
            {
              name: '2',
              type: 'bar',
              stack: 'total',
              color: '#FFABAF',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings2',
            },
            {
              name: '3',
              type: 'bar',
              stack: 'total',
              color: '#A9E1C3',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings3',
            },
            {
              name: '4',
              type: 'bar',
              stack: 'total',
              color: '#69D19C',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings4',
            },
            {
              name: '5',
              type: 'bar',
              stack: 'total',
              color: '#4EAB7D',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings5',
            },
          ],
          yAxisRightConfig: {
            show: true,
          },
          rightTitle: 'Reviews',
        },
      },
      minWidth: 1000,
      columnsConfig: [
        {
          key: 'name',
          header: 'Language',
          align: 'left',
          render: (cvm: any) => dealWithOthersName(cvm.name, 'language'),
          width: 130,
          renderHelper: [languageRenderHelper],
        },
        {
          key: 'star_ratings_total',
          header: 'Rating Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_total),
          width: 130,
        },
        {
          key: 'star_ratings_5',
          header: '5 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_5),
          width: 130,
        },
        {
          key: 'star_ratings_4',
          header: '4 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_4),
          width: 130,
        },
        {
          key: 'star_ratings_3',
          header: '3 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_3),
          width: 130,
        },
        {
          key: 'star_ratings_2',
          header: '2 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_2),
          width: 130,
        },
        {
          key: 'star_ratings_1',
          header: '1 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_1),
          width: 130,
        },
      ],
    },
    {
      title: 'Ratings by Language',
      titleTips: 'keywordDrillUrlRatingChartTips',
      operations: [barSIcon, tableIcon],
      defaultOperation: 'sentiment-s-bar',
      type: 'language_rating',
      chart: {
        sentimentBar: {
          seriesConfig: [
            {
              name: 'Filtered Avg. Rating',
              type: 'bar',
              stack: 'total',
              color: '#5086F3',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'scoreArr',
            },
            {
              name: 'Avg. Rating of Game Store Reviews',
              type: 'bar',
              stack: 'total',
              color: '#A6C9FF',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'lifetimeScore',
            },
          ],
          tooltipConfig: {
            title: 'Rating',
          },
          rightTitle: 'Rating Difference',
          rightTitleTips: 'keywordDrillUrlMobileDifferenceYAxixTips',
          xAxisConfig: {
            min: -5,
            max: 5,
          },
        },
      },
      minWidth: 620,
      columnsConfig: [
        {
          key: 'language',
          header: 'Language',
          align: 'left',
          render: (cvm: any) => dealWithOthersName(cvm.language, 'language'),
          width: 160,
        },
        {
          key: 'lifetime_score',
          header: 'Avg. Rating of Game Store Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.lifetime_score),
          width: 220,
        },
        {
          key: 'score',
          header: 'Filtered Avg. Rating',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.score),
          width: 210,
        },
      ],
    },
  ],
  'app store': [
    {
      title: 'Language',
      operations: [commonBarHIcon, lineIcon, tableIcon],
      defaultOperation: 'common-bar',
      type: 'language',
      chart: {
        commonBar: {
          tooltipConfig: {
            title: 'Reviews',
            showTotal: false,
          },
          seriesConfig: [
            {
              name: '1',
              type: 'bar',
              stack: 'total',
              color: '#F17C86',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings1',
            },
            {
              name: '2',
              type: 'bar',
              stack: 'total',
              color: '#FFABAF',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings2',
            },
            {
              name: '3',
              type: 'bar',
              stack: 'total',
              color: '#A9E1C3',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings3',
            },
            {
              name: '4',
              type: 'bar',
              stack: 'total',
              color: '#69D19C',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings4',
            },
            {
              name: '5',
              type: 'bar',
              stack: 'total',
              color: '#4EAB7D',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'starRatings5',
            },
          ],
          yAxisRightConfig: {
            show: true,
          },
          rightTitle: 'Reviews',
        },
      },
      columnsConfig: [
        {
          key: 'name',
          header: 'Language',
          align: 'left',
          render: (cvm: any) => dealWithOthersName(cvm.name, 'language'),
          width: 130,
          renderHelper: [languageRenderHelper],
        },
        {
          key: 'star_ratings_total',
          header: 'Rating Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_total),
          width: 130,
        },
        {
          key: 'star_ratings_5',
          header: '5 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_5),
          width: 130,
        },
        {
          key: 'star_ratings_4',
          header: '4 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_4),
          width: 130,
        },
        {
          key: 'star_ratings_3',
          header: '3 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_3),
          width: 130,
        },
        {
          key: 'star_ratings_2',
          header: '2 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_2),
          width: 130,
        },
        {
          key: 'star_ratings_1',
          header: '1 Star Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.star_ratings_1),
          width: 130,
        },
      ],
    },
    {
      title: 'Ratings by Language',
      titleTips: 'keywordDrillUrlRatingChartTips',
      operations: [barSIcon, tableIcon],
      defaultOperation: 'sentiment-s-bar',
      type: 'language_rating',
      chart: {
        sentimentBar: {
          seriesConfig: [
            {
              name: 'Filtered Avg. Rating',
              type: 'bar',
              stack: 'total',
              color: '#5086F3',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'scoreArr',
            },
            {
              name: 'Avg. Rating of Game Store Reviews',
              type: 'bar',
              stack: 'total',
              color: '#A6C9FF',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'lifetimeScore',
            },
          ],
          tooltipConfig: {
            title: 'Rating',
          },
          rightTitle: 'Rating Difference',
          rightTitleTips: 'keywordDrillUrlMobileDifferenceYAxixTips',
        },
      },
      minWidth: 620,
      columnsConfig: [
        {
          key: 'language',
          header: 'Language',
          align: 'left',
          render: (cvm: any) => dealWithOthersName(cvm.language, 'language'),
          width: 160,
        },
        {
          key: 'lifetime_score',
          header: 'Avg. Rating of Game Store Reviews',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.lifetime_score),
          width: 220,
        },
        {
          key: 'score',
          header: 'Filtered Avg. Rating',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.score),
          width: 210,
        },
      ],
    },
  ],
  steam: [
    {
      title: 'Reviews by Language',
      operations: [commonBarHIcon, lineIcon, tableIcon],
      defaultOperation: 'common-bar',
      type: 'language',
      chart: {
        commonBar: {
          tooltipConfig: {
            title: 'Mentions',
            showTotal: true,
          },
          seriesConfig: [
            {
              name: 'Positive',
              type: 'bar',
              stack: 'total',
              color: '#69D19C',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'positiveMentions',
            },
            {
              name: 'Negative',
              type: 'bar',
              stack: 'total',
              color: '#F17C86',
              barMaxWidth: 30,
              emphasis: {
                disable: true,
              },
              dataKey: 'negativeMentions',
            },
          ],
          yAxisRightConfig: {
            show: true,
          },
          rightTitle: 'Reviews',
        },
      },
      minWidth: 580,
      columnsConfig: [
        {
          key: 'name',
          header: 'Language',
          align: 'left',
          render: (cvm: any) => dealWithOthersName(cvm.name, 'language'),
          minWidth: 160,
        },
        {
          key: 'positive_mentions+negative_mentions',
          header: 'Reviews',
          align: 'right',
          render: (cvm: any) =>
            reviewsFormat(cvm.positive_mentions + cvm.negative_mentions),
          width: 130,
        },
        {
          key: 'positive_mentions',
          header: 'Positive',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.positive_mentions),
          width: 130,
        },
        {
          key: 'negative_mentions',
          header: 'Negative',
          align: 'right',
          render: (cvm: any) => reviewsFormat(cvm.negative_mentions),
          width: 130,
        },
      ],
    },
    {
      title: 'Positive% by Language',
      titleTips: 'keywordDrillUrlPositiveRatioByLanguageTips',
      operations: [barSIcon, tableIcon],
      defaultOperation: 'sentiment-s-bar',
      type: 'language',
      chart: {
        sentimentBar: {
          seriesConfig: [
            {
              name: 'Filtered Positive%',
              type: 'bar',
              stack: 'total',
              color: '#69D19C',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'positiveMentionsPercent',
            },
            {
              name: 'Steam Review Positive%',
              type: 'bar',
              stack: 'total',
              color: '#C0EBD2',
              barMaxWidth: 30,
              label: {
                show: false,
              },
              emphasis: {
                disable: true,
              },
              dataKey: 'lifetimePositiveMention',
            },
          ],
          tooltipConfig: {
            title: 'Positive%',
            formatValue: (value: number) =>
              percentageFormat(Math.abs(value) * 100),
          },
          rightTitle: 'Positive% Difference',
          rightTitleTips: 'keywordDrillUrlRatingDifferenceYAxixTips',
          yAxisRightConfig: {
            dataKey: 'positiveDifference',
            formatValue: (value: number) => DBO_V.ratio(value * 100),
          },
          xAxisConfig: {
            axisLabelFormat: (value: number) => DBO_Y.percent(Math.abs(value)),
            min: -1,
            max: 1,
          },
        },
      },
      isLifetime: true,
      minWidth: 620,
      columnsConfig: [
        {
          key: 'name',
          header: 'Language',
          align: 'left',
          render: (cvm: any) => dealWithOthersName(cvm.name, 'language'),
          width: 160,
        },
        {
          key: 'lifetime_positive',
          header: 'Steam Review Positive%',
          align: 'right',
          render: (cvm: any) => percentageFormat(cvm.lifetime_positive * 100),
          width: 220,
          numFmt: ['0.00%'],
        },
        {
          key: 'positive',
          header: 'Filtered Avg. Rating',
          align: 'right',
          render: (cvm: any) => percentageFormat(cvm.positive * 100),
          width: 210,
          numFmt: ['0.00%'],
        },
      ],
    },
  ],
};

export type AnalysisTableTypeTab =
  | 'social'
  | 'social_video_posts'
  | 'social_comments'
  | 'comments';

export const analysisTableTab: (
  formatMessage: any,
  filterKeys?: AnalysisTableTypeTab[],
) => {
  text: string | any;
  value: AnalysisTableTypeTab;
}[] = (formatMessage, filterKeys) => {
  const list: {
    text: string | any;
    value: AnalysisTableTypeTab;
  }[] =
    [
      {
        text: (
          <div className="dbo-inlineflex">
            <span className="dbo-analysis-summary-text">
              {formatMessage({ id: 'Total Social Media' })}
            </span>
          </div>
        ),
        value: 'social',
      },
      {
        text: (
          <div className="dbo-inlineflex">
            <span className="dbo-analysis-summary-text">
              {formatMessage({ id: 'Game Store Reviews' })}
            </span>
          </div>
        ),
        value: 'comments',
      },
    ];

  return list?.filter((item) => !filterKeys?.includes(item.value));
};


export const socialTabOptions: (
  formatMessage: any,
) => {
  text: string | any;
  value: AnalysisTableTypeTab;
}[] = (formatMessage) => (
  [
    {
      text: (
        <div className="dbo-inlineflex">
          <span className="dbo-analysis-summary-text">
            {formatMessage({ id: 'All' })}
          </span>
        </div>
      ),
      value: 'social',
    },
    {
      text: (
        <div className="dbo-inlineflex">
          <span className="dbo-analysis-summary-text">
            {formatMessage({ id: 'Video/Post' })}
          </span>
        </div>
      ),
      value: 'social_video_posts',
    },
    {
      text: (
        <div className="dbo-inlineflex">
          <span className="dbo-analysis-summary-text">
            {formatMessage({ id: 'Comments' })}
          </span>
        </div>
      ),
      value: 'social_comments',
    },
  ]
);


export const regularMaximumDuration = (formatMessage: any) => ({
  defaultNum: 30,
  defaultUnit: 'days',
  tips: formatMessage('Only time ranges of 30 days or less are supported.'),
});
